import React from 'react';
import { motion } from 'framer-motion';
import {
    Palette, MousePointer2, Layout, Layers, Box,
    Smartphone, Monitor, PenTool, Lightbulb, UserCheck,
    Sparkles, Zap, ArrowRight, Eye, Command, Component, ChevronLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO_Component from '../../components/SEO';

const UiUxDesign = () => {
    const designPillars = [
        { title: "User Research", desc: "Understanding the human behind the screen.", icon: UserCheck },
        { title: "Wireframing", desc: "Constructing the skeletal map of interaction.", icon: Layout },
        { title: "Visual Design", desc: "Crafting beautiful, high-fidelity interfaces.", icon: Palette },
        { title: "Prototyping", desc: "Bringing static screens to interactive life.", icon: Command },
        { title: "Interaction", desc: "Defining the dance between user and app.", icon: MousePointer2 },
        { title: "Consistency", desc: "Design systems that scale effortlessly.", icon: Component }
    ];

    return (
        <div className="bg-[#02000c] min-h-screen text-white pt-20 overflow-hidden">
            <SEO_Component
                title="UI/UX Design | Intuitive & User-Centric Interfaces - MehareTech"
                description="We create stunning, high-fidelity UI/UX designs that prioritize user experience and brand identity. Expert Figma prototyping, user research, and interactive design solutions."
                keywords="UI UX design agency, user interface design, user experience optimization, Figma design services, web design India, mobile app UI design, conversion rate optimization"
            />

            {/* Back to Services Link */}
            <div className="container mx-auto px-6 mb-4">
                <Link to="/services" className="inline-flex items-center gap-2 text-violet-500 font-bold uppercase tracking-widest text-[10px] hover:text-white transition-colors group">
                    <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                    Back to Services
                </Link>
            </div>

            {/* Hero Section */}
            <section className="relative py-12 px-6">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-violet-600/10 blur-[120px] -z-10"></div>

                <div className="container mx-auto text-center max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-violet-500 font-black uppercase tracking-[0.3em] text-[10px] mb-2 block">
                            Design Philosophy
                        </span>
                        <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tighter">
                            BEYOND VISUALS: <span className="bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">HUMAN FIRST</span>
                        </h1>
                        <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto font-medium">
                            We create interfaces that are not just beautiful, but intuitive and engaging.
                        </p>
                        <div className="flex justify-center">
                            <button className="px-8 py-3 bg-violet-600 text-white font-bold rounded-xl hover:bg-violet-700 transition-all flex items-center gap-2 text-sm group">
                                Start Design Journey <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Pillars Grid */}
            <section className="py-12 px-6 bg-slate-900/40 border-y border-white/5">
                <div className="container mx-auto">
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        {designPillars.map((pillar, i) => (
                            <motion.div
                                key={i}
                                className="p-6 bg-slate-900 border border-white/5 rounded-2xl hover:border-violet-500/30 transition-all"
                            >
                                <pillar.icon size={26} className="text-violet-500 mb-4" />
                                <h3 className="text-lg font-bold mb-1">{pillar.title}</h3>
                                <p className="text-slate-400 text-[11px] leading-relaxed">{pillar.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Philosophy Section */}
            <section className="py-12 px-6">
                <div className="container mx-auto">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <div className="md:w-1/2">
                            <h2 className="text-3xl font-black mb-6 tracking-tight">
                                Intuitive <span className="text-violet-500">Experiences</span>
                            </h2>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                                    <div className="text-violet-500 font-bold mb-1 text-xs">Simplicity</div>
                                    <p className="text-[10px] text-slate-400">Removing friction from every click.</p>
                                </div>
                                <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                                    <div className="text-violet-500 font-bold mb-1 text-xs">Accessibility</div>
                                    <p className="text-[10px] text-slate-400">Design for everyone, everywhere.</p>
                                </div>
                            </div>
                        </div>
                        <div className="md:w-1/2 relative group">
                            <img
                                src="https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg"
                                alt="Design"
                                className="rounded-3xl shadow-xl w-full h-48 object-cover border border-white/10"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default UiUxDesign;
